<script setup>
	import {
		ref,
		computed,
		watch,
	} from 'vue';
	import { DsButton, DsModal } from '@coloplast/design-system';
	import Toaster from 'shared/components/toaster/toaster.vue';
	import ModalLoginCampaign from 'shared/components/ModalLoginCampaign.vue';
	import { useStore } from 'vuex';
	import axios from 'axios';
	import * as _ from 'lodash';
	import { track_cta_button } from 'shared/services/tracking/contentTrackingService';
	import { track_form_submission } from 'shared/services/tracking/formTrackingService';
	import { formatApiMessages } from 'multisite/services/checkoutHelperService.js';
	import initCampaignIdSFDC from 'shared/vanilla/initSendCampaignIDtoSFDC';
	import Events from 'shared/settings/events';

	import "@coloplast/design-system/dist/components/modal/ds-modal.css";

	const store = useStore();

	//we need it if user is not logged in and we show popup first in case we have a button "add sample to basket"
	store.commit('setIsProductCardOnCampaignPage', true);

	const submitUrl = '/api/forms/submit';

	const gtmFormName = computed(() => store.getters['getGtmFormName']);

	//formKit id that shouldn't be submitted one more time
	const
		formKitSubmittedFormId = computed(() => store.getters.getFormKitSubmittedFormId),
		ctaButtonModel = computed(() => {
			if (props?.vm?.CurrentPage?.CtaButton) {
				return props.vm.CurrentPage.CtaButton;
			}
			return props?.vm?.CurrentBlock?.CtaButton ? props.vm.CurrentBlock.CtaButton : props.vm.CurrentBlock;
		}),
		isForDownload = computed(() => {
			return ctaButtonModel.value.IsDownload === 'True';
		}),
		isDownloadedImmediately = computed(() => {
			return ctaButtonModel.value.StartDownloadImmediately === 'True';
		}),
		ctaButtonVariant = computed(() => {
			if (ctaButtonModel.value?.Variant) {
				return ctaButtonModel.value.Variant;
			} else if (props.vm.BaseViewModel?.CtaButtonVariant) {
				return props.vm.BaseViewModel.CtaButtonVariant;
			} else {
				return 'primary';
			}
		});

	const props = defineProps({
		vm: {
			type: Object,
			default: () => { },
		},
		isInlineLayout: {
			type: Boolean,
			default: () => false,
		},
	});

	const
		formId = ref(props?.vm?.BaseViewModel?.FormId),
		showPopup = ref(false),
		showSuccessMsg = ref(false),
		successMsgOptions = ref({}),
		isDownloadButton = ref(false),
		downloadLink = ref(null),
		addItemToBasketRequest = ref(props?.vm?.BaseViewModel?.AddItemToBasketRequest),
		shouldTrackCampaignId = ref(props?.vm?.BaseViewModel?.ShouldTrackSfdcCampaign),
		campaignId = ref(props.vm?.CurrentBlock?.CampaignId || props.vm?.CurrentPage?.CampaignId),
		campaignStatus = ref(props.vm?.CurrentBlock?.CampaignStatus || props.vm?.CurrentPage?.CampaignStatus),
		isIntermediateLoginModalOpen = ref(false),
		isHcpModalLoginCampaign = ref(!!props.vm.BaseViewModel.HcpCampaignLoginModal),
		isPdfDowload = ref(!!props.vm.BaseViewModel.DownloadUrl);

	if (ctaButtonModel.value) {
		downloadLink.value = ctaButtonModel.value.Link || props.vm.BaseViewModel.CtaButtonLinkUrl || ctaButtonModel.value.CtaButtonLink;
	}

	watch(() => formKitSubmittedFormId.value, (newId) => {
		//if that's the form we are using for our cta button re-render it
		if (newId === formId.value) {
			isDownloadButton.value = true;
		}
	}, { immediate: true });

	function activateHidePopup() {
		showPopup.value = !showPopup.value;
		if (showPopup.value) {
			showSuccessMsg.value = false;
			//track campaign id if download Immediately and not add sample to basket
			trackButtonClick(isDownloadedImmediately.value, false);
		}
	}

	async function addSampleAction() {
		const response = await store.dispatch('addItemToBasket', addItemToBasketRequest.value);

		if (response.status === 200) {
			const openFlyoutBasket = new CustomEvent(Events.ACTIVATE_FLY_OUT_BASKET, {});
			document.dispatchEvent(openFlyoutBasket);
		}
	}

	function executeSuccessMessage(successMessages) {
		//track campaign id if not download Immediately because it was already tracked when started downloading
		if (shouldTrackCampaignId.value && !isDownloadedImmediately.value) {
			initCampaignIdSFDC(campaignId.value, campaignStatus.value);
		}

		if (successMessages.displayPopupMessage && (successMessages.successMessageTitle || successMessages.successMessageDescription)) {
			showSuccessMsg.value = true;
			successMsgOptions.value = successMessages;
		}
		//if we get that this form can't be submitted multiple times then add download link to button
		if (!successMessages.displayFormNextTime) {
			store.commit('setFormKitSubmittedFormId', formId.value);
		}

		if (downloadLink.value && !isForDownload.value && !isDownloadedImmediately.value) {
			window.open(downloadLink.value);
		} else if (downloadLink.value && isForDownload.value && !isDownloadedImmediately.value) {
			let link = document.createElement('a');
			link.setAttribute('download', '');
			link.href = downloadLink.value;
			link.click();
		}
	}

	async function submitHandler(formValues, node) {
		let bodyFormData = new FormData();

		_.each(formValues, (value, key) => {
			if (value) {
				bodyFormData.append(key, value);
			}
		});

		await axios
			.post(submitUrl, bodyFormData)
			.then(response => {
				activateHidePopup();
				track_form_submission(gtmFormName.value || 'no_data');
				if (response.data) {
					executeSuccessMessage(response.data);
				}
				else if (addItemToBasketRequest.value) {
					if (shouldTrackCampaignId.value) {
						initCampaignIdSFDC(campaignId.value, campaignStatus.value);
					}
					addSampleAction();
					store.commit('setFormKitSubmittedFormId', formId.value);
				}
			})
            .catch((error) => {
				const
					{ response } = error,
					{ data, status } = response;
				//if phone is not real or another field error
				if (status === 400 && data?.message) {
					let errorObj = {};
					_.each(formatApiMessages(data.message), ({ name, message }) => {
						errorObj[name] = message;
					});
					node.setErrors([], errorObj);
				}
				else {
					activateHidePopup();
				}
			})
	}

	function trackButtonClick(trackCampaignId = true, addSampleToBasket = true) {
		track_cta_button(ctaButtonModel.value.Text || ctaButtonModel.value.CtaButtonText);
		//send campaign id if user is HCP and logged in
		if (shouldTrackCampaignId.value && trackCampaignId) {
			initCampaignIdSFDC(campaignId.value, campaignStatus.value);
		}
		//only when we click button that doesn't contain form anymore then add sample
		if (addItemToBasketRequest.value && addSampleToBasket) {
			addSampleAction();
		}
	}

	function proceedWithLoggedInUserFlow() {
		isDownloadButton.value ? trackButtonClick() : activateHidePopup();
	}

	function activateIntermediateLoginModal() {
		isIntermediateLoginModalOpen.value = true;
	}

	function closeIntermediateModal(intermediateModalValue) {
		isIntermediateLoginModalOpen.value = false;

		if (intermediateModalValue === 'continue') {
			proceedWithLoggedInUserFlow();
		}
	}
</script>

<template>
	<ModalLoginCampaign
		v-if="isHcpModalLoginCampaign"
		:vm="vm.BaseViewModel.HcpCampaignLoginModal"
		:is-modal-open="isIntermediateLoginModalOpen"
		@close-modal="closeIntermediateModal"
	/>
	<DsButton v-if="!addItemToBasketRequest"
			  :variant="ctaButtonVariant"
			  data-testid="cta"
			  :href="isDownloadButton || isDownloadedImmediately ? downloadLink : ''"
			  :icon-start="ctaButtonModel?.IconStart"
			  :icon-end="isPdfDowload ? 'download' : ctaButtonModel?.IconEnd"
			  :target="isDownloadButton ? '_blank' : ''"
			  :size="isInlineLayout ? 'sm' : 'md'"
			  :download="(isForDownload || isDownloadedImmediately) ? true : null"
			  class="c-formkit-modal__interactivebutton"
			  @click="isHcpModalLoginCampaign ? activateIntermediateLoginModal() : proceedWithLoggedInUserFlow()"
			  >
			  <template v-if="isPdfDowload">
				{{ vm.CurrentBlock.DownloadButtonLabel }}
			  </template>
			  <template v-else>
				  {{ ctaButtonModel.Text || ctaButtonModel.CtaButtonText }}
			  </template>
	</DsButton>
	<DsButton v-else
			  :variant="ctaButtonVariant"
			  data-testid="addsampletobasket"
			  :icon-start="ctaButtonModel?.IconStart"
			  :icon-end="ctaButtonModel?.IconEnd || 'shopping-basket'"
			  :size="isInlineLayout ? 'sm' : 'md'"
			  class="c-formkit-modal__interactivebutton"
			  @click="isHcpModalLoginCampaign ? activateIntermediateLoginModal() : proceedWithLoggedInUserFlow()"
			  >
		{{ ctaButtonModel.Text || ctaButtonModel.CtaButtonText }}
	</DsButton>

	<DsModal :isOpen="showPopup"
			 @close-modal="activateHidePopup()"
			 closeLabel="Close"
			 size="lg"
			 class="c-formkit-modal"
			 data-testid="formkit-modal"
			 >
		<section class="c-formkit-modal__section c-formkit-container">
			<slot name="form-kit"
					:submit-handler="submitHandler">
			</slot>
		</section>
	</DsModal>

	<Toaster v-if="showSuccessMsg"
			 class="c-formkit-container__toaster"
			 :description="successMsgOptions?.successMessageDescription"
			 :headline="successMsgOptions?.successMessageTitle"
			 :timeout="successMsgOptions?.successMessageTimeout?.toString()" />
</template>
